import { useCallback, useState } from 'react';
import type { FieldPath, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { ApiError } from '../api/errors';

export interface ServerErrorResult<T extends FieldValues> {
  /** Non-field error message to render in an ErrorBanner. Null means no banner. */
  bannerMessage: string | null;
  /** Call from useMutation onError. Routes the error into the form (per field) or banner (form-level). */
  handleServerError: (err: unknown) => void;
  /** Clear the banner. Call at the start of a new submit so stale errors don't linger. */
  clearBanner: () => void;
  /** Set a banner message manually (e.g. for client-side validation messages). */
  setBannerMessage: (message: string | null) => void;
}

const NETWORK_ERROR_MESSAGE = 'Check your connection and try again.';
const RATE_LIMITED_MESSAGE = 'Too many attempts. Wait a moment, then try again.';
const FALLBACK_MESSAGE = 'Something went wrong. Try again.';

function bracketToDotPath(field: string): string {
  return field.replace(/\[(\d+)\]/g, '.$1').replace(/^\.+/, '');
}

function isNetworkLikeError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  if (err instanceof Error && err.name === 'AbortError') return true;
  if (
    err &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message?: unknown }).message === 'string'
  ) {
    return /failed to fetch|network|offline/i.test((err as { message: string }).message);
  }
  return false;
}

function extractFieldErrors(err: ApiError): Array<{ field: string; message: string }> {
  const raw = err.body?.fieldErrors;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    // Defensive: support legacy/array contracts too.
    return raw
      .filter(
        (item): item is { field: string; message: string } =>
          !!item &&
          typeof (item as { field?: unknown }).field === 'string' &&
          typeof (item as { message?: unknown }).message === 'string',
      )
      .map((item) => ({ field: bracketToDotPath(item.field), message: item.message }));
  }
  if (typeof raw === 'object') {
    return Object.entries(raw)
      .filter(
        (entry): entry is [string, string] =>
          typeof entry[0] === 'string' && typeof entry[1] === 'string',
      )
      .map(([field, message]) => ({ field: bracketToDotPath(field), message }));
  }
  return [];
}

export function useServerErrorHandler<T extends FieldValues>(
  form: UseFormReturn<T>,
): ServerErrorResult<T> {
  const [bannerMessage, setBannerMessage] = useState<string | null>(null);

  const clearBanner = useCallback(() => {
    setBannerMessage(null);
  }, []);

  const handleServerError = useCallback(
    (err: unknown): void => {
      if (err instanceof ApiError) {
        if (err.status === 401 && err.sessionExpired) {
          // Session-expired redirect is owned by AuthContext; do not double-surface.
          return;
        }
        if (err.status === 429) {
          setBannerMessage(RATE_LIMITED_MESSAGE);
          return;
        }

        const fieldErrors = extractFieldErrors(err);
        if (fieldErrors.length > 0) {
          for (const { field, message } of fieldErrors) {
            form.setError(field as Path<T> as FieldPath<T>, { type: 'server', message });
          }
          // Add a banner ONLY if the body has a top-level message that's distinct
          // from the field messages. Otherwise the field errors stand on their own.
          const topLevel =
            typeof err.body?.message === 'string' && err.body.message.length > 0
              ? err.body.message
              : null;
          if (topLevel && !fieldErrors.some((f) => f.message === topLevel)) {
            setBannerMessage(topLevel);
          } else {
            setBannerMessage(null);
          }
          return;
        }

        // No field errors → entire error becomes banner.
        const message =
          (typeof err.body?.message === 'string' && err.body.message) ||
          (typeof err.body?.error === 'string' && err.body.error) ||
          err.message ||
          FALLBACK_MESSAGE;
        setBannerMessage(message);
        return;
      }

      if (isNetworkLikeError(err)) {
        setBannerMessage(NETWORK_ERROR_MESSAGE);
        return;
      }

      if (err instanceof Error && err.message) {
        setBannerMessage(err.message);
        return;
      }

      setBannerMessage(FALLBACK_MESSAGE);
    },
    [form],
  );

  return {
    bannerMessage,
    handleServerError,
    clearBanner,
    setBannerMessage,
  };
}

export default useServerErrorHandler;
